import { Epic } from 'redux-observable'
import { EMPTY, from, of } from 'rxjs'
import { filter, mergeMap, pluck } from 'rxjs/operators'
import { showToastNotification } from 'shared/store/actions/toastNotification'
import {
  createConvoSidebarSectionAsync,
  deleteConvoSidebarSectionAsync,
  sharedSectionChangedNotification,
  updateConvoSidebarSectionAsync,
} from 'shared/store/actions/user/sections'

import { isActionOf, RootAction, RootState, Services } from 'typesafe-actions'

import { fetchSectionMemberships } from '../user/sections/api'

export const showCreateSidebarToastAfterSectionCreated: Epic<
  RootAction,
  RootAction,
  RootState,
  Services
> = (action$) =>
  action$.pipe(
    filter(isActionOf(createConvoSidebarSectionAsync.success)),
    pluck('payload'),
    mergeMap((payload) =>
      of(
        showToastNotification({
          body: `Created conversation section ${payload.config.title}`,
        })
      )
    )
  )

export const showUpdateSidebarToastAfterSectionUpdated: Epic<
  RootAction,
  RootAction,
  RootState,
  Services
> = (action$) =>
  action$.pipe(
    filter(isActionOf(updateConvoSidebarSectionAsync.success)),
    pluck('payload'),
    filter((data) => !data.hideToast),
    mergeMap(() =>
      of(
        showToastNotification({
          body: 'Updated conversation section successfully',
        })
      )
    )
  )

export const showDeleteSidebarToastAfterSectionCreated: Epic<
  RootAction,
  RootAction,
  RootState,
  Services
> = (action$) =>
  action$.pipe(
    filter(isActionOf(deleteConvoSidebarSectionAsync.success)),
    pluck('payload'),
    mergeMap((payload) =>
      of(
        showToastNotification({
          body: `Deleted conversation section ${payload.title}`,
        })
      )
    )
  )

export const showFailureToastAfterSectionFails: Epic<
  RootAction,
  RootAction,
  RootState,
  Services
> = (action$) =>
  action$.pipe(
    filter(
      isActionOf([
        createConvoSidebarSectionAsync.failure,
        deleteConvoSidebarSectionAsync.failure,
        updateConvoSidebarSectionAsync.failure,
      ])
    ),
    pluck('payload'),
    mergeMap(() =>
      of(
        showToastNotification({
          body: 'Something went wrong.',
        })
      )
    )
  )

export const showSharedToastAfterSectionCreated: Epic<
  RootAction,
  RootAction,
  RootState,
  Services
> = (action$) =>
  action$.pipe(
    filter(isActionOf(sharedSectionChangedNotification)),
    pluck('payload'),
    mergeMap((payload) => {
      const { updatedSections, prevSections } = payload
      return from(prevSections).pipe(
        mergeMap((prevSection) => {
          if (prevSection) {
            const subscriptionId = prevSection.subscriptionId
            const updatedSection = updatedSections.find(
              (section) => section.subscriptionId === subscriptionId
            )
            if (prevSection.eventType === 'CREATE') {
              return of(
                showToastNotification({
                  body: `You were added to the shared section ${updatedSection?.title}.`,
                })
              )
            } else if (prevSection.eventType === 'DELETE') {
              if (prevSection.entityType) {
                if (prevSection.section) {
                  //compare memberships -- if the author of the section or an individual is a part of a team that is removed,
                  //we don't want to update their redux or notify them they've been removed, since they haven't been.
                  return from(
                    fetchSectionMemberships(prevSection.section.id).catch(() => {
                      return []
                    })
                  ).pipe(
                    mergeMap((memberships) => {
                      const currentUserId = prevSection.currentUserId
                      const userTeams = prevSection.currentUsersTeams
                      if (prevSection.section) {
                        //no memberships found means the section was entirely removed
                        if (memberships.length < 1) {
                          return of(
                            deleteConvoSidebarSectionAsync.success({
                              id: prevSection.section.id,
                              title: prevSection.section.title,
                              shared: true,
                            }),
                            showToastNotification({
                              body: `Shared section ${prevSection.section?.title} was deleted.`,
                            })
                          )
                        }
                        if (
                          !memberships.some(
                            (m) =>
                              m.entityId === currentUserId ||
                              userTeams.some((team) => team.id === m.entityId)
                          )
                        ) {
                          return of(
                            deleteConvoSidebarSectionAsync.success({
                              id: prevSection.section.id,
                              title: prevSection.section.title,
                              shared: true,
                            }),
                            showToastNotification({
                              body: `You were removed from the shared section ${prevSection.section.title}.`,
                            })
                          )
                        }
                      }
                      return EMPTY
                    })
                  )
                }
                return EMPTY
              }
            } else if (prevSection.section?.title !== updatedSection?.title) {
              return of(
                showToastNotification({
                  body: `Shared section ${prevSection.section?.title} was renamed ${updatedSection?.title}.`,
                })
              )
            } else {
              return of(
                showToastNotification({
                  body: `Shared section ${prevSection.section?.title} was updated.`,
                })
              )
            }
          }
          return of([])
        })
      )
    })
  )
